<template>
  <v-card class="rounded-xl" :border="false" :elevation="0">
    <v-card-title class="d-flex justify-center align-center">
      <h1 class="title">
        {{ $t('INFORMATION_REPAIR_FILE.TITLE') }}
      </h1>
    </v-card-title>

    <v-row no-gutters class="mt-2 d-flex flex-column ga-4">
      <v-col v-if="transporterModality" cols="12">
        <section class="information-wrapper">
          <p class="information-label">
            {{ $t('INFORMATION_REPAIR_FILE.CONTENT') }}
            <span class="information-content">
              {{ $t('INFORMATION_REPAIR_FILE.TRANSPORTER_MODALITY.' + transporterModality) }}
            </span>
          </p>
        </section>
      </v-col>

      <v-col cols="12">
        <section class="mb-2">
          <h2 class="information-title">
            {{ $t('INFORMATION_REPAIR_FILE.REPAIR_CENTER.TITLE') }}
          </h2>
        </section>

        <section class="information-wrapper">
          <p class="information-label">
            {{ $t('INFORMATION_REPAIR_FILE.REPAIR_CENTER.NAME') }}
            <span class="information-content">
              {{ formatData(repairer?.name) }}
            </span>
          </p>
        </section>

        <section class="information-wrapper">
          <p class="information-label">
            {{ $t('INFORMATION_REPAIR_FILE.REPAIR_CENTER.ADDRESS') }}
            <span class="information-content">
              {{ repairerFullAddress }}
            </span>
          </p>
        </section>

        <section class="information-wrapper">
          <p class="information-label">
            {{ $t('INFORMATION_REPAIR_FILE.REPAIR_CENTER.PHONE') }}
            <span class="information-content">
              {{ formatData(repairer?.phone_number) }}
            </span>
          </p>
        </section>

        <section
          v-if="currentRepairFile.folder_type === FolderTypeConstant.REPAIR_FOLDER"
          class="information-wrapper"
        >
          <p class="information-label">
            {{ $t('INFORMATION_REPAIR_FILE.REPAIR_CENTER.EMAIL') }}
            <span class="information-content">
              {{ formatData(repairer?.contact_email) }}
            </span>
          </p>
        </section>

        <section
          v-if="currentRepairFile.folder_type === FolderTypeConstant.PROSPECT_FOLDER"
          class="information-wrapper"
        >
          <p class="information-label">
            {{ $t('INFORMATION_REPAIR_FILE.REPAIR_CENTER.EMAIL') }}
            <span class="information-content">
              {{ currentRepairFile.prospect_repairer_email_contact }}
            </span>
          </p>
        </section>

        <section
          v-if="currentRepairFile?.initial_erp_repairer_number && repairer"
          class="information-wrapper"
        >
          <p class="information-label">
            {{ $t('INFORMATION_REPAIR_FILE.REPAIR_CENTER.ERP_REPAIRER_NUMBER') }}
            <span class="information-content">
              {{ currentRepairFile?.initial_erp_repairer_number }}
            </span>
          </p>
        </section>

        <section
          v-if="currentRepairFile?.repairer_estimate?.repairer_number && repairer"
          class="information-wrapper"
        >
          <p class="information-label">
            {{ $t('INFORMATION_REPAIR_FILE.REPAIR_CENTER.REPAIRER_NUMBER') }}
            <span class="information-content">
              {{ currentRepairFile?.repairer_estimate?.repairer_number }}
            </span>
          </p>
        </section>
      </v-col>

      <v-col cols="12">
        <section class="mb-1">
          <h2 class="information-title">
            {{ $t('INFORMATION_REPAIR_FILE.STORE.TITLE') }}
          </h2>
        </section>

        <section class="information-wrapper">
          <p class="information-label">
            {{ $t('INFORMATION_REPAIR_FILE.STORE.EMAIL') }}
            <span class="information-content">
              {{ formatData(repairer?.store_contact_email) }}
            </span>
          </p>
          <button
            v-if="clipboardSupported && repairer"
            @click.prevent="copyStoreContactToClipboard"
            class="ml-auto copy-button"
          >
            <v-icon icon="mdi-content-copy" color="#767676" size="18" />
          </button>
        </section>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import TransportModalityConstant from '@/constants/transportModalityConstant'
import RepairCenterService from '@/services/RepairCenterService'
import { RepairFile } from '@/models/RepairFile'
import FolderTypeConstant from '@/constants/repairModule/FolderTypeConstant'
import { displayToast } from '@/components/utils/globalFunction'

export default {
  name: 'InformationRepairFile',
  props: {
    currentRepairFile: RepairFile
  },
  data() {
    return {
      transporterModality: null,
      repairer: null,
      clipboardSupported: false
    }
  },
  computed: {
    FolderTypeConstant() {
      return FolderTypeConstant
    },
    repairerFullAddress() {
      return this.repairer
        ? this.repairer.address_number +
            ' ' +
            this.repairer.address_street +
            ', ' +
            this.repairer.address_zip_code +
            ' ' +
            this.repairer.address_city
        : this.$t('COMMON.NO_VALUE')
    }
  },
  async created() {
    await this.checkRepairFileModality()
  },
  mounted() {
    this.clipboardSupported = !!navigator.clipboard
  },
  methods: {
    async checkRepairFileModality() {
      if (this.currentRepairFile && this.currentRepairFile.initial_transporter_modality) {
        if (
          this.currentRepairFile.initial_transporter_modality ===
          TransportModalityConstant.TRANSPORTER
        ) {
          this.transporterModality = TransportModalityConstant.TRANSPORTER
          await this.getRepairCenterInfo()
        }
        if (
          this.currentRepairFile.initial_transporter_modality === TransportModalityConstant.WORKSHOP
        ) {
          this.transporterModality = TransportModalityConstant.WORKSHOP
          await this.getRepairCenterInfo()
        }
        if (
          this.currentRepairFile.initial_transporter_modality === TransportModalityConstant.STORE
        ) {
          this.transporterModality = TransportModalityConstant.STORE
        }
        if (
          this.currentRepairFile.initial_transporter_modality ===
          TransportModalityConstant.EXCLUDING_SUPPORT
        ) {
          this.transporterModality = TransportModalityConstant.EXCLUDING_SUPPORT
        }
      }
    },
    async getRepairCenterInfo() {
      this.repairer = await RepairCenterService.getRepairCenterDetailByRepairFile(
        this.currentRepairFile.technical_id
      )
    },
    formatData(data) {
      return this.repairer ? data : this.$t('COMMON.NO_VALUE')
    },
    copyStoreContactToClipboard() {
      if (!navigator.clipboard) {
        return
      }

      navigator.clipboard
        .writeText(this.repairer.store_contact_email)
        .then(() => {
          displayToast(this.$t('INFORMATION_REPAIR_FILE.CLIPBOARD_COPY'), 'success')
        })
        .catch((error) => {
          console.error('Clipboard copy failed', error)
          displayToast(this.$t('INFORMATION_REPAIR_FILE.CLIPBOARD_COPY_ERROR'), 'error')
        })
    }
  }
}
</script>

<style scoped>
.v-card {
  padding: 20px;
}

.title,
.information-title {
  font-style: normal;
  line-height: normal;
}

.title {
  font-size: 18px;
  font-weight: 900;
}

.information-title {
  font-size: 16px;
  font-weight: 900;
}

.information-wrapper {
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 2px;
}

.information-label,
.information-content {
  font-size: 13px;
  font-style: normal;
  line-height: 20px;
}

.information-content {
  font-weight: 600;
}

.copy-button {
  cursor: pointer;
  opacity: 1;
}

.copy-button:hover {
  opacity: 0.8;
  transition: opacity 0.3s ease-in-out;
}
</style>
